import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import useWallet from "hooks/useWallet";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getContestsState, setNewContestEntryState } from "state/slices/contests-slice";
import AnimatedBalanceDisplay from "components/common/animations/AnimatedBalanceDisplay";
import { getWalletState } from "state/slices/wallet-slice.ts";

interface Props {
  contestEntryId: string;
}

export const ContestEntryBalanceView = ({ contestEntryId }: Props) => {
  const { balance, formattedBalance, loading: balanceLoading, getWalletData } = useWallet({
    contestEntryId,
  });
  const { staleWallet } = useAppSelector(getWalletState);

  useEffect(() => {
    if (staleWallet) {
      getWalletData();
    }
  }, [staleWallet, getWalletData]);

  const dispatch = useAppDispatch();

  const { newContestEntry } = useAppSelector(getContestsState);

  const [isAnimatingBalance, setIsAnimatingBalance] = useState(false);

  // Refs to hold the latest values for the cleanup function
  const isAnimatingRef = useRef(isAnimatingBalance);
  const newContestEntryRef = useRef(newContestEntry);

  // Keep refs updated
  useEffect(() => {
    isAnimatingRef.current = isAnimatingBalance;
  }, [isAnimatingBalance]);

  useEffect(() => {
    newContestEntryRef.current = newContestEntry;
  }, [newContestEntry]);

  // Effect to decide whether to start the animation
  useEffect(() => {
    if (newContestEntry) {
      if (!balanceLoading && formattedBalance && contestEntryId === newContestEntry?.id && newContestEntry.bannerHasBeenShown) {
        // Start animation only if the Redux state indicates it hasn't run/completed yet
        setIsAnimatingBalance(!newContestEntry.balanceHasBeenAnimated);
      }
    } else {
      // Reset if there's no new entry
      setIsAnimatingBalance(false);
    }
  }, [balanceLoading, formattedBalance, contestEntryId, newContestEntry]); // Depends on Redux state

  // Effect for handling unmount during animation
  useEffect(() => {
    // Return the cleanup function
    return () => {
      // Check the ref's value at the time of unmount
      if (isAnimatingRef.current) {
        const currentEntry = newContestEntryRef.current;
        // Ensure we have the entry data to update
        if (currentEntry) {
          console.log("Unmounting while animating, updating Redux state for", currentEntry.id);
          // Dispatch the update to mark animation as done/initiated
          dispatch(setNewContestEntryState({
            ...currentEntry,
            balanceHasBeenAnimated: true,
          }));
        }
      }
    };
  }, [dispatch]); // dispatch is stable, so this effect runs once on mount, cleanup on unmount

  // Callback for when the animation component finishes naturally
  const handleAnimationComplete = useCallback(() => {
    setIsAnimatingBalance(false);
    // Update Redux state upon successful completion
    if (newContestEntry) {
      dispatch(setNewContestEntryState({
        ...newContestEntry,
        balanceHasBeenAnimated: true,
      }));
    }
    // Include newContestEntry and dispatch in dependencies
  }, [dispatch, newContestEntry]);

  const renderBalance = () => {
    if (newContestEntry && !newContestEntry.bannerHasBeenShown) {
      return <BlockyHeavyText style={{ color: "white", display: "flex", alignItems: "center" }}>
        <Spinner animation="border" size="sm" style={{ marginBottom: "2px" }} />
      </BlockyHeavyText>;
    }
    if (isAnimatingBalance && balance !== 0 && formattedBalance) { // Added null check for formattedBalance
      return (
        <AnimatedBalanceDisplay
          targetBalance={formattedBalance} // Use checked value
          onAnimationComplete={handleAnimationComplete}
        />
      );
    } else if (balanceLoading) {
      return (
        <BlockyHeavyText style={{ color: "var(--secondary-color)", display: "flex", alignItems: "center" }}>
          <Spinner animation="border" size="sm" style={{ marginBottom: "2px" }} />
        </BlockyHeavyText>
      );
    } else if (formattedBalance) {
      return (
        <BlockyHeavyText
          style={{
            fontWeight: "bold",
            fontSize: 14,
            marginRight: 10,
            color: "var(--secondary-color)",
            display: "flex",
            alignItems: "center",
          }}
        >
          {formattedBalance}
        </BlockyHeavyText>
      );
    } else {
      return null;
    }
  };

  return (
    <div style={{ margin: 8, display: "flex", alignItems: "flex-end" }}>
      <BlockyHeavyText
        style={{
          fontSize: 12,
          marginRight: 5,
          color: "var(--sparket-off-white)",
        }}
      >
        Balance:&nbsp;
      </BlockyHeavyText>
      {renderBalance()}
    </div>
  );
};

export default ContestEntryBalanceView;
